// =============================================================================
// PREVIOUS IMPLEMENTATION — kept verbatim below for reference, intentionally
// inert. Line comments rather than a /* */ block: the original text contains
// comment terminators, which would close a block comment early.
//
// The active implementation follows after this block. Nothing above it is live
// code — it is here so the previous behaviour stays readable in place.
// =============================================================================

// import React, { FC, useState } from "react";
// import StepFooter from "../StepFooter";
// import NoticeBar from "../NoticeBar";
// import { PrinterIcon } from "../icons";
// import { PanelProps } from "./types";
// import DocumentDropzone, { MAX_SIZE_MB } from "../../DocumentDropzone";
// import { useLocale } from "../../i18n/locale";
//
// /**
//  * Step 2, the screen the desktop design was drawn against: one accepted-document
//  * note, one large dropzone, and the print-and-delete disclosure underneath.
//  */
// const IdentityPanel: FC<PanelProps> = ({ onBack, onContinue }) => {
//   const { t } = useLocale();
//   const [file, setFile] = useState<File | null>(null);
//   const copy = t.desktop.identityPanel;
//
//   const handleContinue = () => {
//     if (!file) return;
//     // TODO: upload to the verification endpoint before advancing.
//     onContinue?.();
//   };
//
//   return (
//     <>
//       <NoticeBar title={copy.acceptedTitle} body={copy.acceptedBody} />
//
//       <DocumentDropzone
//         id="workspace-id-document"
//         className="mt-6"
//         variant="wide"
//         prompt={copy.dropTitle}
//         browsePrefix={copy.browsePrefix}
//         browseLabel={copy.browse}
//         hint={copy.formats(MAX_SIZE_MB)}
//         file={file}
//         onFileChange={setFile}
//       />
//
//       <div className="mt-6 flex gap-3">
//         <span className="mt-0.5 flex-none text-neutral-400">
//           <PrinterIcon className="h-4 w-4" />
//         </span>
//         <p className="text-[12px] leading-relaxed text-neutral-500">
//           <span className="font-semibold text-navy-900">
//             {copy.safetyLabel}
//           </span>{" "}
//           {copy.safetyBody}
//         </p>
//       </div>
//
//       <StepFooter
//         onBack={onBack}
//         onContinue={handleContinue}
//         label={t.desktop.steps.identity.cta}
//         disabled={!file}
//       />
//     </>
//   );
// };
//
// export default IdentityPanel;

// ==== ACTIVE IMPLEMENTATION ==================================================

import React, { FC, useState } from "react";
import PanelHeading from "../PanelHeading";
import StepFooter from "../StepFooter";
import NoticeBar from "../NoticeBar";
import { useOnboardingRecord } from "../onboardingContext";
import { submitIdentity } from "../onboardingConfig";
import { PanelProps } from "./types";
import DocumentDropzone, { MAX_SIZE_MB } from "../../DocumentDropzone";
import { useLocale } from "../../i18n/locale";

/**
 * Step 2. One accepted-document note, one large dropzone, and the
 * print-and-delete disclosure underneath.
 *
 * The chosen file lives on the page rather than here, so stepping away and
 * coming back does not silently discard it.
 */
const IdentityPanel: FC<PanelProps> = ({
  state,
  update,
  onBack,
  onContinue,
}) => {
  const { t } = useLocale();
  const onboarding = useOnboardingRecord();
  const c = t.desktop.identityPanel;
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  /**
   * A document already on file — filed on an earlier visit, or uploaded a
   * moment ago in this one. The record is not re-fetched after an upload, so
   * this session's own filename has to be remembered in state or the step goes
   * blank again as soon as the tenant navigates away and back.
   */
  const alreadySent = state.identityFileName || onboarding?.identity.file_name || "";
  const provided = Boolean(state.identityFile) || Boolean(alreadySent);

  /**
   * Send the document, then advance.
   *
   * Nothing moves until the DMS has the file. The gate was commented out so the
   * flow could be walked without uploading anything, which meant a tenant could
   * reach the lease without ever having proved who they were.
   */
  const handleContinue = async () => {
    if (alreadySent || !state.identityFile || !onboarding) {
      onContinue?.();
      return;
    }

    setSending(true);
    setError("");
    const saved = await submitIdentity(
      onboarding.generated_contract_pdf_id,
      state.identityFile
    );
    setSending(false);

    if (!saved) {
      setError(t.dropzone.uploadFailed);
      return;
    }
    update({ identityFileName: saved.file_name });
    onContinue?.();
  };

  return (
    <>
      <PanelHeading eyebrow={c.eyebrow} title={c.title} subtitle={c.subtitle} />

      <NoticeBar
        className="mt-6"
        title={c.acceptedTitle}
        body={c.acceptedBody}
      />

      <DocumentDropzone
        id="workspace-id-document"
        className="mt-5"
        variant="wide"
        prompt={c.dropTitle}
        readyTitle={c.readyTitle}
        browsePrefix={c.browsePrefix}
        browseLabel={c.browse}
        hint={c.formats(MAX_SIZE_MB)}
        file={state.identityFile}
        // A File cannot survive a reload, so a tenant returning to this step —
        // by the rail or after refreshing — would otherwise find the dropzone
        // empty even though the DMS already holds their document.
        savedFileName={alreadySent}
        onFileChange={(identityFile) => update({ identityFile })}
      />

      <div className="flex gap-3 px-[3px] py-[17px] text-[11px] leading-[1.5] text-[#89939c] min-[760px]:text-[12px]">
        <span className="flex-none text-gold" aria-hidden="true">
          ▣
        </span>
        <p className="m-0">
          <strong className="font-bold text-[#66737d]">{c.safetyLabel}</strong>{" "}
          {c.safetyBody}
        </p>
      </div>

      {error && (
        <p
          role="alert"
          className="mb-3 border border-[#d8a49c] bg-[#fbf0ee] p-[11px_13px] text-[11px] text-[#8c3a30]"
        >
          {error}
        </p>
      )}

      <StepFooter
        onBack={onBack}
        onContinue={handleContinue}
        label={c.cta}
        // Re-enabled: the step exists to collect a document, so it cannot be
        // walked past without one. The server refuses an empty submission too.
        disabled={!provided}
        busy={sending}
      />
    </>
  );
};

export default IdentityPanel;
